/**
 * This module handles saving and loading named objects to the browser's localStorage.
 * It uses a consistent keying scheme: `locatia-[type]-[name]`.
 * For example, a passage plan named "My Trip" would be saved under "locatia-ppp-My Trip".
 * A search pattern named "Test Pattern" would be "locatia-psp-Test Pattern".
 */

const KEY_PREFIX = 'locatia';

/**
 * Represents a single item stored in local storage.
 */
export interface StoredItem<T> {
    name: string;
    timestamp: string;
    data: T;
}

/**
 * Saves a data object to local storage with a given type and name.
 *
 * @param type - The type of the object (e.g., 'ppp' for Passage Plan, 'psp' for Search Pattern).
 * @param name - The user-defined name for the object.
 * @param data - The data object to save. It will be JSON.stringified.
 */
export function saveToLocalStorage<T>(type: 'ppp' | 'psp', name: string, data: T): void {
    if (!name) {
        console.error("Cannot save to local storage: name is required.");
        return;
    }
    const key = `${KEY_PREFIX}-${type}-${name}`;
    const itemToStore = {
        name,
        timestamp: new Date().toISOString(),
        data,
    };
    try {
        localStorage.setItem(key, JSON.stringify(itemToStore));
    } catch (e) {
        console.error(`Failed to save item "${name}" to local storage:`, e);
    }
}

/**
 * Loads a specific data object from local storage by its type and name.
 *
 * @param type - The type of the object ('ppp' or 'psp').
 * @param name - The name of the object to load.
 * @returns The stored item (including name, timestamp, and data), or null if not found or on error.
 */
export function loadFromLocalStorage<T>(type: 'ppp' | 'psp', name: string): StoredItem<T> | null {
    const key = `${KEY_PREFIX}-${type}-${name}`;
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        return JSON.parse(raw) as StoredItem<T>;
    } catch (e) {
        console.error(`Failed to load item "${name}" from local storage:`, e);
        return null;
    }
}

/**
 * Deletes a specific item from local storage.
 *
 * @param type - The type of the object ('ppp' or 'psp').
 * @param name - The name of the object to delete.
 */
export function deleteFromLocalStorage(type: 'ppp' | 'psp', name:string): void {
    const key = `${KEY_PREFIX}-${type}-${name}`;
    localStorage.removeItem(key);
}


/**
 * Lists all stored items of a specific type.
 *
 * @param type - The type of items to list ('ppp' or 'psp').
 * @returns An array of stored items.
 */
export function listLocalStorageItems<T>(type: 'ppp' | 'psp'): StoredItem<T>[] {
    const items: StoredItem<T>[] = [];
    const prefix = `${KEY_PREFIX}-${type}-`;
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
            try {
                const raw = localStorage.getItem(key);
                if (raw) {
                    items.push(JSON.parse(raw));
                }
            } catch (e) {
                console.error(`Failed to parse item with key "${key}" from local storage:`, e);
            }
        }
    }
    // Sort by timestamp, newest first
    return items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}
